import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import useAuth from "@/hooks/useAuth";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { groupPortfoliosByType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { validateKeywordsSize, MAX_KEYWORDS_COUNT, MAX_KEYWORDS_TOTAL_CHARS } from "@/pages/PortfolioBuilder/keywordLimits";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import * as Sentry from "@sentry/react";
import { derivedViewColor, nextViewColor } from "@/shared/utils/savedViewHighlight";

import { toCategories, type Category } from "./tagCategories";

/**
 * Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the form
 * without entering edit mode; saving still creates a new filter.
 *
 * A concierge filter is a UNION across axes (tags OR watchlists), so the
 * watchlist ids matter as much as the tags — carrying only `tags` silently
 * halves the filter's reach.
 */
export interface FilterDraft {
  name?: string;
  searchKeyword?: string;
  tags?: string[];
  /** Ticker/keyword watchlists this filter matches against. */
  portfolioIds?: number[];
  /** Tag watchlists this filter matches against. */
  tagsWatchlistIds?: number[];
  filterMode?: "AND" | "OR";
  keywordOp?: "AND" | "OR";
  /** Typed keyword vs keyword-watchlist terms. Needed for an exact copy of a
   *  library filter; everything else leaves it at the default. */
  keywordWatchlistOp?: "AND" | "OR";
}

export interface UseFilterBuilderOptions {
  queryId?: number | null;
  draft?: FilterDraft | null;
  /**
   * Lists to build against instead of the signed-in user's own.
   *
   * Only the guided demo passes this. It opens this editor on a recorded plan
   * for a logged-out visitor, whose usePortfoliosCRUD is empty — so the lists
   * the draft selects would have no rows to sit in and the left pane would
   * come up blank next to a query referring to them. Same override PlanPreview
   * takes, and for the same reason.
   */
  portfoliosOverride?: Portfolio[];
  /** `id` is absent only for the guided demo, which writes nothing. */
  onSaved?: (queryName: string, categories: string[], id?: number) => void;
  /**
   * Desk fields, mirrored from the one form the builder pane shows. Absent for
   * everyone else, in which case the payload is unchanged.
   */
  desk?: {
    publishToLibrary: boolean;
    description: string;
    whenToUse: string;
    categories: string[];
  };
  onSaveFailed?: () => void;
  onDeleted?: () => void;
}

export function useFilterBuilder(options: UseFilterBuilderOptions) {
  const confirm = useConfirm();
  const { authenticated } = useAuth();
  const { queriesQuery, createMutation, editMutation, deleteMutation } = useHeadlineQueriesCRUD();
  const { portfolios: ownPortfolios } = usePortfoliosCRUD();
  // The hook still runs unconditionally; the override just wins. Logged out it
  // resolves to an empty list anyway, so this costs nothing in the demo case.
  const portfolios = options.portfoliosOverride ?? ownPortfolios;
  const { watchlistsQuery: tagsWatchlistsQuery } = useTagsWatchlistsCRUD();
  const tagsWatchlists = tagsWatchlistsQuery.data || [];
  const headlineQueries = queriesQuery.data;

  // ── Form state ────────────────────────────────────────────────────────
  const [queryName, setQueryName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTagsWatchlistIds, setSelectedTagsWatchlistIds] = useState<number[]>([]);
  const [filterMode, setFilterMode] = useState<"AND" | "OR">("OR");
  const [keywordOp, setKeywordOp] = useState<"AND" | "OR">("OR");
  const [keywordWatchlistOp, setKeywordWatchlistOp] = useState<"AND" | "OR">("OR");
  const [notifications, setNotifications] = useState<FilterNotifications>(emptyNotifications());
  /**
   * Highlight colour the user picked. Null means "not picked" — see
   * displayColor for what stands in, and what gets saved, until they do.
   */
  const [color, setColor] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditing = options.queryId != null;

  // ── Portfolio grouping ────────────────────────────────────────────────
  const selectedGrouped = useMemo(() => {
    const selected = portfolios.filter((p) => selectedPortfolioIds.includes(p.id));
    return groupPortfoliosByType(selected);
  }, [portfolios, selectedPortfolioIds]);

  const hasBothWatchlistTypes =
    selectedGrouped.tickerOnly.length > 0 && selectedGrouped.keywordOnly.length > 0;

  useEffect(() => {
    if (hasBothWatchlistTypes && keywordOp === "OR") {
      setKeywordOp("AND");
    }
  }, [hasBothWatchlistTypes]);

  // Resolve selected tag watchlist objects
  const selectedTagWatchlistObjects = useMemo(
    () => tagsWatchlists.filter((tw) => selectedTagsWatchlistIds.includes(tw.id)),
    [tagsWatchlists, selectedTagsWatchlistIds]
  );

  // ── Keyword validation ────────────────────────────────────────────────
  const aggregatedKeywords = useMemo(() => {
    const fromWatchlists = selectedGrouped.keywordOnly.flatMap((p) => p.keywords ?? []);
    const typed = searchKeyword.trim();
    return typed ? [...fromWatchlists, typed] : fromWatchlists;
  }, [selectedGrouped.keywordOnly, searchKeyword]);

  const keywordsSize = validateKeywordsSize(aggregatedKeywords);

  const hasKeywords = searchKeyword.trim().length > 0 || selectedGrouped.keywordOnly.length > 0;
  const hasTickerWatchlists = selectedGrouped.tickerOnly.length > 0;
  const hasTags = selectedCategories.length > 0 || selectedTagsWatchlistIds.length > 0;
  const hasWatchlistsOrTags = hasTickerWatchlists || hasTags;

  // ── Counts per type ───────────────────────────────────────────────────
  const tickerCount = selectedGrouped.tickerOnly.length;
  const keywordCount = selectedGrouped.keywordOnly.length + (searchKeyword.trim() ? 1 : 0);
  const tagCount = selectedCategories.length + selectedTagsWatchlistIds.length;

  // ── Load existing query for editing ───────────────────────────────────
  useEffect(() => {
    if (!isEditing || !headlineQueries) return;
    const q = headlineQueries.find((query) => query.id === options.queryId);
    if (!q) return;

    setQueryName(q.queryName);
    setSearchKeyword(q.searchKeyword ?? "");
    setSelectedPortfolioIds(q.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(q.tagsWatchlistIds ?? []);
    setFilterMode(q.filterMode ?? "OR");
    setKeywordOp(q.keywordOp ?? "OR");
    setKeywordWatchlistOp(q.keywordWatchlistOp ?? "OR");
    setColor(q.color ?? null);
    // Keep any extra backend sources from the API — PATCH replaces the whole map
    setNotifications(q.notifications ?? emptyNotifications());

    // Convert category IDs back to Category objects
    setSelectedCategories(toCategories(q.categories ?? []));
  }, [isEditing, options.queryId, headlineQueries]);

  // ── Seed from a concierge draft (create mode only) ────────────────────
  useEffect(() => {
    const draft = options.draft;
    if (!draft || isEditing) return;
    setQueryName(draft.name ?? "");
    setSearchKeyword(draft.searchKeyword ?? "");
    if (draft.filterMode) setFilterMode(draft.filterMode);
    if (draft.keywordOp) setKeywordOp(draft.keywordOp);
    if (draft.keywordWatchlistOp) setKeywordWatchlistOp(draft.keywordWatchlistOp);
    setSelectedCategories(toCategories(draft.tags ?? []));
    // The other half of the union — without these a "tags [Fed] OR
    // watchlists [Fed Speakers]" filter arrives as tags only.
    setSelectedPortfolioIds(draft.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(draft.tagsWatchlistIds ?? []);
  }, [options.draft, isEditing]);

  /**
   * What the picker shows until they pick.
   *
   * A new list gets the next palette colour, and is saved with it — it has no
   * id yet, so the id-derived colour it would otherwise fall back to cannot be
   * previewed. An existing list with no stored colour shows the derived one
   * it is already highlighting in, and keeps it unless changed.
   */
  const ownQueryCount =
    headlineQueries?.filter((q) => q.owner_type === "USER").length ?? 0;
  const displayColor =
    color ??
    (isEditing
      ? derivedViewColor(options.queryId ?? undefined)
      : nextViewColor(ownQueryCount));
  const colorToSave = isEditing ? color : displayColor;

  // ── Toggle helpers ────────────────────────────────────────────────────
  const togglePortfolio = useCallback((id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) => {
      const exists = prev.some((c) => c.id === cat.id);
      return exists ? prev.filter((c) => c.id !== cat.id) : [...prev, cat];
    });
  }, []);

  const toggleTagWatchlist = useCallback((id: number) => {
    setSelectedTagsWatchlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const clearAll = useCallback(() => {
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
  }, []);

  const reset = useCallback(() => {
    setQueryName("");
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setNotifications(emptyNotifications());
    setColor(null);
    setFormErrors({ categories: "", keywords: "" });
  }, []);

  // ── Semantic summary ──────────────────────────────────────────────────
  const semanticSummary = useMemo(() => {
    const parts: string[] = [];

    // Keywords
    const kwParts: string[] = [];
    if (searchKeyword.trim()) kwParts.push(`"${searchKeyword.trim()}"`);
    selectedGrouped.keywordOnly.forEach((p) => kwParts.push(p.name));
    if (kwParts.length > 0) {
      const joiner = keywordWatchlistOp === "AND" ? " and " : " or ";
      parts.push(`containing ${kwParts.join(joiner)}`);
    }

    // Tickers
    if (selectedGrouped.tickerOnly.length > 0) {
      const names = selectedGrouped.tickerOnly.map((p) => p.name);
      parts.push(`mentioning ${names.join(" or ")} tickers`);
    }

    // Tags
    const tagParts: string[] = [];
    if (selectedCategories.length > 0) tagParts.push(...selectedCategories.map((c) => c.title));
    if (selectedTagWatchlistObjects.length > 0) tagParts.push(...selectedTagWatchlistObjects.map((tw) => tw.name));
    if (tagParts.length > 0) {
      parts.push(`tagged ${tagParts.join(" or ")}`);
    }

    if (parts.length === 0) return "No filters selected";
    const joiner = keywordOp === "AND" ? " AND " : " OR ";
    return "Headlines " + parts.join(joiner);
  }, [searchKeyword, selectedGrouped, selectedCategories, selectedTagWatchlistObjects, keywordOp, keywordWatchlistOp]);

  // ── Submit ────────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({ categories: "", keywords: "" });

  const handleSubmit = useCallback(async () => {
    setFormErrors({ categories: "", keywords: "" });

    if (
      selectedCategories.length === 0 &&
      selectedPortfolioIds.length === 0 &&
      selectedTagsWatchlistIds.length === 0 &&
      searchKeyword.trim() === ""
    ) {
      setFormErrors((prev) => ({
        ...prev,
        categories: "Select at least one: keywords, watchlist, or tags.",
      }));
      return;
    }

    if (hasKeywords && !keywordsSize.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: keywordsSize.error ?? "Keyword limit exceeded.",
      }));
      return;
    }

    const payload = {
      queryName,
      categories: selectedCategories.map((cat) => String(cat.id ?? "")),
      searchKeyword,
      portfolioIds: selectedPortfolioIds,
      tagsWatchlistIds: selectedTagsWatchlistIds,
      filterMode,
      keywordOp,
      keywordWatchlistOp,
      notifications,
      // Omitted when not picked on an edit, so the stored value is left alone.
      // A half-typed hex from the text field is not a colour.
      color:
        colorToSave && /^#[0-9a-fA-F]{6}$/.test(colorToSave)
          ? colorToSave
          : undefined,
      description: options.desk?.description.trim() || null,
      whenToUse: options.desk?.whenToUse.trim() || null,
      assetClasses: options.desk?.categories,
      // Create-only; the server ignores owner_type on PATCH.
      ...(options.desk?.publishToLibrary
        ? ({ owner_type: "NEWSQUAWK" } as const)
        : {}),
    };

    try {
      if (!authenticated) {
        /**
         * Guided demo: nothing is written.
         *
         * POST /headline-queries needs an account and would 401, so the button
         * that ends the demo's most important step failed for exactly the
         * audience it was built for. Nothing to create and nothing to clean up
         * either — the combination is already in the panels via demoWatchlist,
         * so "saving" it reports the truth about what an account would do.
         * Same shape as useWatchlistCreator's unauthenticated branch.
         */
        toast.success("Watchlist created");
        options.onSaved?.(payload.queryName, payload.categories);
        return;
      }
      let savedId: number | undefined;
      if (isEditing) {
        await editMutation.mutateAsync({ id: options.queryId as number, ...payload });
        toast.success("Filter updated");
        savedId = options.queryId as number;
      } else {
        const response = await createMutation.mutateAsync({ id: undefined, ...payload });
        toast.success("Filter created");
        savedId = Number(response.data?.id);
      }
      options.onSaved?.(payload.queryName, payload.categories, savedId);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to save filter");
      options.onSaveFailed?.();
    }
  }, [
    queryName, selectedCategories, searchKeyword, selectedPortfolioIds,
    selectedTagsWatchlistIds, filterMode, keywordOp, keywordWatchlistOp,
    notifications, colorToSave, isEditing, options.queryId, hasKeywords, keywordsSize,
    editMutation, createMutation, options.onSaved, options.onSaveFailed,
    options.desk, authenticated,
  ]);

  const handleDelete = useCallback(async () => {
    if (!options.queryId) return;
    const ok = await confirm({ title: "Delete Filter", message: "This filter will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok) return;

    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(options.queryId);
      toast.success("Filter deleted");
      options.onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete filter");
    } finally {
      setIsDeleting(false);
    }
  }, [options.queryId, deleteMutation, options.onDeleted]);

  return {
    // Form state
    queryName, setQueryName,
    searchKeyword, setSearchKeyword,
    selectedPortfolioIds, setSelectedPortfolioIds,
    selectedCategories, setSelectedCategories,
    selectedTagsWatchlistIds, setSelectedTagsWatchlistIds,
    filterMode, setFilterMode,
    keywordOp, setKeywordOp,
    keywordWatchlistOp, setKeywordWatchlistOp,
    notifications, setNotifications,
    color, setColor, displayColor,

    // Toggles
    togglePortfolio,
    toggleCategory,
    toggleTagWatchlist,
    clearAll,
    reset,

    // Derived
    isEditing,
    selectedGrouped,
    selectedTagWatchlistObjects,
    semanticSummary,
    keywordsSize,
    hasKeywords,
    hasTickerWatchlists,
    hasTags,
    hasWatchlistsOrTags,
    tickerCount,
    keywordCount,
    tagCount,
    formErrors,

    // Data
    portfolios,
    tagsWatchlists,

    // Mutations
    handleSubmit,
    handleDelete,
    isSubmitting: createMutation.isPending || editMutation.isPending,
    isDeleting,

    // Keyword limits
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
  };
}

export type UseFilterBuilderReturn = ReturnType<typeof useFilterBuilder>;
